'use client';

import {
  useCallback,
  useEffect,
  useState,
  createContext,
  useMemo,
  useContext,
} from 'react';
import { SORABAN_YEAR_COOKIE } from '@/_constants/cookies';
import { CURRENT_TAX_YEAR } from '@/_constants/year-options';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { getFullName } from '@/_utils/user';
import { getTriggeredSections } from '@/_utils/sections';
import { removeGivenQuestionType } from '@/_utils/questions';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getUser, getPreviewClientQuestionnaires } from '@/_api/admin-users';
import { getCompanyTaxReturns } from '@/_api/admin-companies';
import { setCookie, getCookie } from 'cookies-next/client';
import { sortClientQuestionnaires } from '@/_utils/questionnaire';
import { useParams, useSearchParams } from 'next/navigation';
import { getCompanyConversation } from '@/_api/client-conversations';
import { getQuestionnaireTemplate } from '@/_api/admin-questionnaire_templates';
import type { Company } from '@/_types/models/company';
import type { Conversation } from '@/_types/models/conversations';
import type { QuestionnaireTemplate } from '@/_types/models/questionnaireTemplate';
import type { Question, Questionnaire } from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';
import type { FirmUserAsAdvisor, User } from '@/_types/models/user';
import type { UserPreviewProviderProps } from './UserPreviewProvider.types';
import { TaxReturn } from '@/_types/models/taxReturns';
import { getUserCompanies } from '@/_api/v2/admin-users';

const UserPreviewContext = createContext<UserPreviewProviderProps>({
  isPreviewLoading: true,
  onAddClientEntries: () => null,
  onMarkSectionComplete: () => null,
  onRemoveClientEntry: () => null,
  previewAdvisor: undefined,
  previewCompany: undefined,
  previewConversation: undefined,
  previewFullName: '',
  previewQuestionnaires: [],
  previewTemplate: undefined,
  previewTemplateSection: undefined,
  previewTemplateSections: [],
  previewTemplateSelectedQuestions: [],
  previewUser: undefined,
  previewUserCompanies: [],
  previewUserCompanyHash: undefined,
  previewUserCompanyId: undefined,
  previewUserTaxReturns: [],
  previewYearFilter: CURRENT_TAX_YEAR,
  setPreviewYearFilter: () => null,
  updateTemplateQuestion: () => null,
});

const UserPreviewProvider = (props: any) => {
  const { token, user } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);

  const [isPreviewUserLoading, setPreviewUserLoading] = useState<boolean>(true);
  const [isPreviewTaxReturnsLoading, setPreviewTaxReturnsLoading] =
    useState<boolean>(true);
  const [isPreviewConversationsLoading, setPreviewConversationsLoading] =
    useState<boolean>(true);
  const [_isPreviewQuestionnaireLoading, setPreviewQuestionnaireLoading] =
    useState<boolean>(true);
  const [isPreviewQuestionnairesLoading, setPreviewQuestionnairesLoading] =
    useState<boolean>(true);
  const [isPreviewUserCompaniesLoading, setPreviewUserCompaniesLoading] =
    useState<boolean>(true);
  const [previewUser, setPreviewUser] = useState<User | undefined>(undefined);
  const [taxReturns, setTaxReturns] = useState<Array<TaxReturn>>([]);
  const [questionnaires, setQuestionnaires] = useState<Array<Questionnaire>>(
    [],
  );
  const [previewConversation, setPreviewConversation] = useState<
    Conversation | undefined
  >(undefined);
  const [previewTemplate, setPreviewTemplate] = useState<
    QuestionnaireTemplate | undefined
  >(undefined);
  const [previewUserCompanies, setPreviewUserCompanies] = useState<
    Array<Company>
  >([]);

  const searchParams = useSearchParams();
  const previewCompanyHashSearchParam = searchParams.get('previewCompanyHash');
  const { previewCompanyHash, previewUserId, previewQuestionnaireId } =
    useParams();

  const previewSectionId: number = useMemo(
    () =>
      parseInt(searchParams.get('sectionId') as string) ||
      previewTemplate?.sections?.[0]?.id ||
      -1,
    [previewTemplate?.sections, searchParams],
  );

  const yearCookie: string = getCookie(SORABAN_YEAR_COOKIE) || CURRENT_TAX_YEAR;

  const [previewYearFilter, setPreviewYearFilter] = useState<string>(
    yearCookie || CURRENT_TAX_YEAR,
  );

  const onSetPreviewYearFilter = (newYear: string) => {
    setPreviewYearFilter(newYear);
    setCookie(SORABAN_YEAR_COOKIE, newYear);
  };

  const onAddClientEntries = (questionId: number, setId: number): void => {
    try {
      const followupQuestions: Array<Question> =
        previewTemplate?.questionnaire_questions
          ?.filter(
            (x) =>
              x.follow_up_question_id === questionId &&
              x.trigger_for_follow_up_question === 'Template',
          )
          .map((q, key) => {
            const newId: number = Date.now() + key;

            return {
              ...q,
              id: newId,
              trigger_for_follow_up_question: (
                1000 +
                questionId +
                setId
              ).toString(),
            } as any as Question;
          }) || [];

      const updatedTemplateQuestions: Array<Question> = [
        ...(previewTemplate?.questionnaire_questions?.map((q) =>
          q.id === questionId
            ? { ...q, answer: 'ADMIN_PREVIEW_MOCK_ANSWER' }
            : q,
        ) || []),
        ...followupQuestions,
      ];

      const newTemplate: QuestionnaireTemplate = {
        ...previewTemplate!,
        questionnaire_questions: updatedTemplateQuestions,
      };

      setPreviewTemplate(newTemplate);
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onRemoveClientEntry = (questionId: number, setId: string) => {
    try {
      const remainingFollowUps: Array<Question> =
        previewTemplate?.questionnaire_questions?.filter(
          (q) =>
            q.follow_up_question_id === questionId &&
            q.trigger_for_follow_up_question !== setId.toString() &&
            q.trigger_for_follow_up_question !== 'Template',
        ) || [];

      const shouldClearParentAnswer: boolean = !remainingFollowUps?.length;

      const updatedTemplateQuestions: Array<Question> =
        previewTemplate?.questionnaire_questions
          ?.filter(
            (question) =>
              !(
                question.follow_up_question_id === questionId &&
                question.trigger_for_follow_up_question === setId.toString()
              ),
          )
          .map((q) => {
            if (shouldClearParentAnswer && q.id === questionId) {
              return { ...q, answer: '' };
            }
            return q;
          }) || [];

      const newTemplate: QuestionnaireTemplate = {
        ...previewTemplate!,
        questionnaire_questions: updatedTemplateQuestions,
      };

      setPreviewTemplate(newTemplate);
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onMarkSectionComplete = (sectionId: number) => {
    if (!previewTemplate?.id) return;

    const newSections =
      previewTemplate?.sections?.map((s) => {
        if (s.id === sectionId) s.marked_completed = true;

        return s;
      }) || [];

    const newTemplate: QuestionnaireTemplate = {
      ...previewTemplate!,
      sections: newSections,
    };

    return setPreviewTemplate(newTemplate);
  };

  const updateTemplateQuestion = async ({
    questionId,
    answer,
    answerBy,
    companyId: _,
  }: {
    questionId: number;
    answer: string;
    answerBy?: string | null;
    companyId?: number | null;
  }) => {
    try {
      const _updateQuestion = (q: Question) => {
        return q.id === questionId
          ? { ...q, answer, answer_by: answerBy || null }
          : q;
      };

      const updatedTemplateQuestions: Array<Question> =
        previewTemplate?.questionnaire_questions?.map(_updateQuestion) || [];

      const updatedTemplateSections: Array<Section> =
        previewTemplate?.sections?.map((s) => {
          const updatedQuestions =
            s?.questionnaire_questions?.map(_updateQuestion) || [];

          s.questionnaire_questions = updatedQuestions;

          return s;
        }) || [];

      setPreviewTemplate((previous) => ({
        ...previous!,
        sections: updatedTemplateSections,
        questionnaire_questions: updatedTemplateQuestions,
      }));
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const previewUserCompanyHash: string | undefined = useMemo(() => {
    return (
      previewCompanyHash?.toString() ||
      previewCompanyHashSearchParam?.toString() ||
      previewUser?.default_company?.hash_id
    );
  }, [
    previewUser?.default_company?.hash_id,
    previewCompanyHash,
    previewCompanyHashSearchParam,
  ]);

  const previewUserCompanyId: number | undefined | null = useMemo(() => {
    return (
      previewUserCompanies?.find(
        (company) => company?.hash_id === previewUserCompanyHash,
      )?.id || previewUser?.default_company_id
    );
  }, [
    previewUserCompanyHash,
    previewUserCompanies,
    previewUser?.default_company_id,
  ]);

  const previewAdvisor = useMemo(() => {
    const advisor: FirmUserAsAdvisor = {
      id: user?.id ?? -1,
      avatar: user?.medium_url,
      name: user?.full_name || '',
    };

    return advisor;
  }, [user]);

  const previewCompany: Company | undefined = useMemo(() => {
    if (!previewUserCompanies?.length) return undefined;

    const selectedCompany = previewUserCompanies?.find(
      (c) => c.id === previewUserCompanyId,
    );

    return selectedCompany;
  }, [previewUserCompanyId, previewUserCompanies]);

  const previewFullName: string = useMemo(
    () => getFullName(previewUser),
    [previewUser],
  );

  const previewQuestionnaires: Array<Questionnaire> = useMemo(() => {
    return !!previewYearFilter && questionnaires && questionnaires?.length
      ? questionnaires
          ?.filter?.((q) =>
            previewYearFilter ? q.tax_year === parseInt(previewYearFilter) : q,
          )
          ?.sort(sortClientQuestionnaires) || []
      : [];
  }, [questionnaires, previewYearFilter]);

  const previewUserTaxReturns: Array<TaxReturn> = useMemo(() => {
    return taxReturns
      ? taxReturns
          ?.filter?.((tr) => tr.tax_year === parseInt(previewYearFilter))
          ?.filter(
            (tr) =>
              tr.draft_status === 'draft' ||
              tr.draft_status === 'change_requested' ||
              (tr.status !== 'processing' && tr.status !== 'action_required'),
          )
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          )
      : [];
  }, [previewYearFilter, taxReturns]);

  const previewTemplateSection: Section | undefined = useMemo(
    () =>
      previewTemplate?.sections?.find(
        (section) => section.id === previewSectionId,
      ),
    [previewSectionId, previewTemplate?.sections],
  );

  const previewTemplateSections: Array<Section> = useMemo(() => {
    const previewableSections: Array<Section> = getTriggeredSections(
      previewTemplate?.sections,
      previewTemplate?.questionnaire_questions,
    )?.map((section) => {
      if (
        section?.section_key === 'agreement_signature' ||
        section?.section_type === 2
      ) {
        section.marked_completed = true;
      }

      return section;
    });

    return previewableSections || [];
  }, [previewTemplate?.sections, previewTemplate?.questionnaire_questions]);

  const previewTemplateSelectedQuestions: Array<Question> = useMemo(() => {
    const raw: Array<Question> =
      previewTemplate?.questionnaire_questions?.filter(
        (question) => question.section_id === previewSectionId,
      ) || [];
    const hasMoreSections: boolean = Boolean(
      (previewTemplateSections?.length ?? 0) > 1,
    );

    return hasMoreSections && preferences?.hide_document_uploads_within_sections
      ? removeGivenQuestionType(raw, 'upload')
      : raw;
  }, [
    previewSectionId,
    previewTemplate?.questionnaire_questions,
    previewTemplateSections?.length,
    preferences?.hide_document_uploads_within_sections,
  ]);

  const isPreviewLoading: boolean = Boolean(
    isPreviewUserLoading ||
      isPreviewConversationsLoading ||
      isPreviewQuestionnairesLoading ||
      isPreviewTaxReturnsLoading ||
      isPreviewUserCompaniesLoading,
  );

  const _loadPreviewUser = useCallback(async () => {
    if (!token) return;

    try {
      setPreviewUserLoading(true);

      const user = await getUser({
        token,
        userId: parseInt(previewUserId!.toString()),
      });
      setPreviewUser(user);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setPreviewUserLoading(false);
    }
  }, [addToast, previewUserId, token]);

  const _loadPreviewUserQuestionnaires = useCallback(async () => {
    if (!token) return;

    try {
      setPreviewQuestionnairesLoading(true);

      const questionnaires = await getPreviewClientQuestionnaires({
        token,
        companyId: previewUserCompanyId!,
      });
      setQuestionnaires(questionnaires);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setPreviewQuestionnairesLoading(false);
    }
  }, [addToast, previewUserCompanyId, token]);

  const _loadPreviewUserTaxReturns = useCallback(async () => {
    if (!token) return;

    try {
      setPreviewTaxReturnsLoading(true);

      const taxReturns = await getCompanyTaxReturns({
        token,
        companyId: previewUserCompanyId!,
      });

      setTaxReturns(taxReturns);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setPreviewTaxReturnsLoading(false);
    }
  }, [addToast, previewUserCompanyId, token]);

  const _loadPreviewUserConversations = useCallback(async () => {
    if (!token || !previewUserCompanyId) return;

    try {
      setPreviewConversationsLoading(true);

      const companyConversation = await getCompanyConversation({
        companyId: previewUserCompanyId,
        token,
      });

      setPreviewConversation(companyConversation);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setPreviewConversationsLoading(false);
    }
  }, [addToast, previewUserCompanyId, token]);

  const _loadPreviewUserCompanies = useCallback(async () => {
    if (!token || typeof previewUser?.id !== 'number') return;

    try {
      setPreviewUserCompaniesLoading(true);

      const previewUserCompanies = await getUserCompanies({
        token,
        userId: previewUser?.id!,
      });

      if (Array.isArray(previewUserCompanies) && !previewUserCompanies?.error) {
        setPreviewUserCompanies(previewUserCompanies);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setPreviewUserCompaniesLoading(false);
    }
  }, [addToast, previewUser?.id, token]);

  const _loadPreviewQuestionnaire = useCallback(async () => {
    if (!token || !previewQuestionnaireId) return;

    try {
      setPreviewQuestionnaireLoading(true);

      const questionnaireTemplate = await getQuestionnaireTemplate({
        questionnaireTemplateId: parseInt(previewQuestionnaireId?.toString()),
        token,
      });

      setPreviewTemplate(questionnaireTemplate);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setPreviewQuestionnaireLoading(false);
    }
  }, [addToast, previewQuestionnaireId, token]);

  useEffect(() => {
    if (!!previewUserId) {
      _loadPreviewUser();
      _loadPreviewUserConversations();
      _loadPreviewUserCompanies();
    }
  }, [
    _loadPreviewUser,
    _loadPreviewUserConversations,
    _loadPreviewUserCompanies,
    previewUserId,
  ]);

  useEffect(() => {
    if (!!previewUserCompanyId) {
      _loadPreviewUserQuestionnaires();
      _loadPreviewUserTaxReturns();
    }
  }, [
    _loadPreviewUserQuestionnaires,
    _loadPreviewUserTaxReturns,
    previewUserCompanyId,
  ]);

  useEffect(() => {
    if (!!previewQuestionnaireId) {
      _loadPreviewQuestionnaire();
    }
  }, [_loadPreviewQuestionnaire, previewQuestionnaireId]);

  return (
    <UserPreviewContext
      value={{
        isPreviewLoading,
        onAddClientEntries,
        onMarkSectionComplete,
        onRemoveClientEntry,
        previewAdvisor,
        previewCompany,
        previewConversation,
        previewFullName,
        previewQuestionnaires,
        previewTemplate,
        previewTemplateSection,
        previewTemplateSections,
        previewTemplateSelectedQuestions,
        previewUser,
        previewUserCompanies,
        previewUserCompanyHash,
        previewUserCompanyId,
        previewUserTaxReturns,
        previewYearFilter,
        setPreviewYearFilter: onSetPreviewYearFilter,
        updateTemplateQuestion,
      }}
    >
      {props.children}
    </UserPreviewContext>
  );
};

export { UserPreviewContext, UserPreviewProvider };
